import type { Directive, DirectiveBinding } from 'vue';

import {
  CONTEXT_PARAMS,
  type AnalyticsParams,
  type TrackEvent,
  type TrackScopeParams,
} from './events';
import type { Tracker } from './Tracker';
import { resolveTrigger, type Modifiers, type TrackTrigger } from './triggers';

export type TrackValue = TrackEvent | null | undefined;
export type TrackScopeValue = TrackScopeParams | null | undefined;

class TrackBinding {
  private constructor(
    readonly value: TrackEvent,
    readonly trigger: TrackTrigger,
  ) {}

  static from(
    value: TrackValue,
    modifiers: Modifiers = {},
  ): TrackBinding | null {
    return value ? new TrackBinding(value, resolveTrigger(modifiers)) : null;
  }
}

function closestElement(origin: Node | null): Element | null {
  return origin instanceof Element ? origin : origin?.parentElement ?? null;
}

export class TrackRegistry {
  private readonly bindings = new WeakMap<Element, TrackBinding>();

  register(element: Element, binding: TrackBinding | null): void {
    if (binding) this.bindings.set(element, binding);
    else this.bindings.delete(element);
  }

  unregister(element: Element): void {
    this.bindings.delete(element);
  }

  resolve(
    target: EventTarget | null,
  ): { element: Element; binding: TrackBinding } | null {
    let element = target instanceof Element ? target : null;

    // The tracked element closest to the target wins, one event per click.
    while (element) {
      const binding = this.bindings.get(element);
      if (binding) return { element, binding };
      element = element.parentElement;
    }

    return null;
  }
}

export class TrackScopeRegistry {
  private readonly scopes = new WeakMap<Element, TrackScopeParams>();

  register(element: Element, scope: TrackScopeValue): void {
    if (scope) this.scopes.set(element, scope);
    else this.scopes.delete(element);
  }

  unregister(element: Element): void {
    this.scopes.delete(element);
  }

  /** Params of every scope around `origin`; inner scopes win. */
  collect(origin: Node | null): AnalyticsParams {
    const params: AnalyticsParams = {};

    for (let el = closestElement(origin); el; el = el.parentElement) {
      Object.entries(this.scopes.get(el) ?? {}).forEach(([key, value]) => {
        if (value !== undefined && !(key in params)) params[key] = value;
      });
    }

    return params;
  }
}

/** Completes a tracked event with its context: href and enclosing scopes. */
export class TrackPayloadResolver {
  constructor(private readonly scopes: TrackScopeRegistry) {}

  resolve({ event, params }: TrackEvent, origin: Node | null): AnalyticsParams {
    const element = closestElement(origin);
    const needsLink = (CONTEXT_PARAMS[event] as readonly string[]).includes(
      'link_url',
    );
    const link: AnalyticsParams = needsLink
      ? { link_url: element?.closest('a[href]')?.getAttribute('href') ?? '' }
      : {};

    const own = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined),
    ) as AnalyticsParams;

    return { ...link, ...this.scopes.collect(element), ...own };
  }
}

export class ClickTrackingListener {
  private root: Document | null = null;

  constructor(
    private readonly registry: TrackRegistry,
    private readonly resolver: TrackPayloadResolver,
    private readonly tracker: Tracker,
  ) {}

  attach(root: Document = document): void {
    this.detach();
    this.root = root;
    root.addEventListener('click', this.handleClick, { capture: true });
  }

  detach(): void {
    this.root?.removeEventListener('click', this.handleClick, {
      capture: true,
    });
    this.root = null;
  }

  private handleClick = (click: MouseEvent): void => {
    const tracked = this.registry.resolve(click.target);
    if (!tracked) return;

    const { element, binding } = tracked;
    if (binding.trigger.shouldFire(click, element)) {
      this.tracker.track(
        binding.value.event,
        this.resolver.resolve(binding.value, element),
      );
    }
  };
}

export function createTrackDirective(
  registry: TrackRegistry,
): Directive<HTMLElement, TrackValue> {
  const register = (
    element: HTMLElement,
    { value, modifiers }: DirectiveBinding<TrackValue>,
  ) => registry.register(element, TrackBinding.from(value, modifiers));

  return {
    mounted: register,
    updated: register,
    beforeUnmount: element => registry.unregister(element),
  };
}

export function createTrackScopeDirective(
  scopes: TrackScopeRegistry,
): Directive<HTMLElement, TrackScopeValue> {
  const register = (
    element: HTMLElement,
    { value }: DirectiveBinding<TrackScopeValue>,
  ) => scopes.register(element, value);

  return {
    mounted: register,
    updated: register,
    beforeUnmount: element => scopes.unregister(element),
  };
}
